import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import Footer from "@/components/Footer";
import UpsellToast from "@/components/UpsellToast";
import CockpitSidebar from "@/components/CockpitSidebar";
import MarketingBanner from "@/components/marketing/MarketingBanner";
import CampaignModal from "@/components/marketing/CampaignModal";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";
import { useViewAs } from "@/context/ViewAsContext";
import { useAuth } from "@/context/AuthContext";
// Sprint V98.1 (Do 17.09.2026 20:30) — Webinar-Banner global ins Layout,
// damit er auf ALLEN Dashboard-Seiten fuer Free-User sichtbar ist.
import FreeWebinarBanner from "@/components/dashboard/FreeWebinarBanner";
import { journeyFixesOn } from "@/lib/journeyFlags";

/**
 * DashboardLayout — gemeinsamer Wrapper aller /dashboard/*-Routen.
 * - lädt Dashboard-Daten via /api/user/dashboard
 * - leitet bei 401 zu /login?next={current_path}
 * - reicht die Daten via Render-Prop an die Kinder weiter
 */
export default function DashboardLayout({ children, refreshKey = 0 }) {
  const navigate = useNavigate();
  const { override } = useViewAs();
  const { clearUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const { data } = await api.get("/user/dashboard");
      setData(data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        // Sprint V102.i.2 — Session-Loop-Fix.
        // Zuerst AuthContext invalidieren, damit /login den User NICHT als
        // eingeloggt behandelt und sofort wieder auf /cockpit redirectet.
        // Ohne diesen Aufruf entsteht ein sichtbarer Flacker-Loop, wenn
        // das Backend-Cookie/JWT abgelaufen ist, der User im Browser aber
        // noch das gecachte user-Objekt aus /auth/me hat.
        if (typeof clearUser === "function") clearUser();
        if (journeyFixesOn() && window.location.pathname.startsWith("/login")) return;
        const next = encodeURIComponent(window.location.pathname);
        navigate(`/login?next=${next}`, { replace: true });
        return;
      }
      setError("Konnte den Mitgliederbereich nicht laden. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  }, [navigate, clearUser]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  // v2.9.1 — DEV View-as: Override des primary_fokustyp im weitergereichten data-Objekt.
  // Greift transparent für ALLE Konsumenten (Dashboard, FokustypDetail, CoCreator, …).
  // WICHTIG: Hook MUSS vor allen konditionalen Returns aufgerufen werden (Rules of Hooks).
  const effectiveData = useMemo(() => {
    if (!data || !override) return data;
    return {
      ...data,
      user: { ...data.user, primary_fokustyp: override },
    };
  }, [data, override]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--fts-neutral-50)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[var(--fts-primary-bright)] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--fts-neutral-50)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-slate-600">{error || "Keine Daten."}</p>
          <button
            onClick={loadData}
            data-testid="dashboard-retry-btn"
            className="mt-4 rounded-lg bg-[var(--fts-primary-bright)] text-white px-4 py-2 text-sm font-semibold"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--fts-neutral-50)] flex flex-col" data-testid="dashboard-layout">
      <DashboardHeader
        firstName={effectiveData.user?.first_name}
        statusLabel={effectiveData.user?.account_status_label}
        statusColor={effectiveData.user?.account_status_color}
        licenseTier={effectiveData.user?.license_tier}
        licenseStatus={effectiveData.user?.license_status}
        licenseData={effectiveData.user}
      />
      {/* Sprint 6 HOCH 6 AC #3 — „Zurück zum Cockpit"-Button auf allen
          Dashboard-Unterseiten. Wird nur angezeigt, wenn NICHT direkt /dashboard. */}
      <BackToDashboardBar />
      {/* Sprint V98.1 (Do 17.09.2026 20:30) — Webinar-Banner GLOBAL fuer alle
          Free-User auf jeder Dashboard-Seite. Prominent, damit er Aufmerksamkeit
          bekommt. Rendert intern nichts fuer Pro-User (isFree-Check). */}
      <div className="max-w-6xl mx-auto w-full px-6 lg:px-8 pt-4">
        <FreeWebinarBanner user={effectiveData.user} variant="prominent" />
      </div>
      {/* Sprint V102.j (So 27.09.2026) — Marketing-Banner (Trial-Push u.a.).
          Wiederverwendbare Kampagnen-Slot oberhalb der Fold. Rendert intern
          nichts, wenn kein aktiver Banner fuer User-Tier oder User dismissed. */}
      <div className="max-w-6xl mx-auto w-full px-6 lg:px-8 pt-3">
        <MarketingBanner />
      </div>
      {/* Sprint v1.7 Phase 4 (Do 03.09.2026) — Cockpit-Sidebar links permanent
          (nur ≥lg-Viewport). Enthält 3 Phasen + VideoKurs. Kein Selfmanagement-
          Toolbox-Top-Level-Punkt (Addon in Trainings/Kurse post-launch). */}
      <div className="flex flex-1">
        <CockpitSidebar />
        <main className="flex-1 min-w-0">
          {typeof children === "function" ? children({ data: effectiveData, reload: loadData }) : children}
        </main>
      </div>
      <Footer />
      <UpsellToast />
      {/* Sprint V102.j — In-App-Campaign-Modal (Trial-Push u.a.).
          Rendert intern nichts, wenn keine Kampagne fuer User aktiv/dismissed. */}
      <CampaignModal />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sprint 6 HOCH 6 AC #3 — Sub-Page-Back-Link.
// Wird auf jeder /dashboard/*-Subroute angezeigt (z.B. /dashboard/abo,
// /dashboard/anliegen, /dashboard/fokustyp/:slug). Auf /dashboard selbst
// nicht — dort ist der Header-Logo-Link der Anker.
// ---------------------------------------------------------------------------
function BackToDashboardBar() {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "");
  const isDashboardRoot = path === "/cockpit" || path === "";
  if (isDashboardRoot) return null;
  return (
    <div
      className="max-w-6xl mx-auto w-full px-6 lg:px-8 pt-5"
      data-testid="back-to-dashboard-bar"
    >
      <Link
        to="/cockpit"
        data-testid="back-to-dashboard-link"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zum Mitgliederbereich
      </Link>
    </div>
  );
}
